import React, { useMemo } from 'react';
import styles from './AvgScoresChar.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { StudentMetricsDashboardAverageScoresPerSkill } from '@/school-management/types/generate-report';
import { getSkillName, skillsForReport } from '../normalizeData';

interface AvgScoresCharProps {
  data?: StudentMetricsDashboardAverageScoresPerSkill[];
  onSkillSelect?: (skillId: number) => void;
}

const AvgScoresChar: React.FC<AvgScoresCharProps> = ({ data, onSkillSelect }) => {
  const { t } = useAdminTranslation();
  const getSkillLabel = (skillId: number) => {
    const name = getSkillName(skillId).toString();
    const key = `studentMetricsDashboard.drillDown.skills.columns.skill${name.charAt(0).toUpperCase()}${name.slice(1).toLowerCase()}`;
    return t(key) || name;
  };
  const getScoreColor = (score: number) =>
    score < 60 ? '#E11515' : score < 80 ? '#FF9F4C' : '#2BAE19';

  const skillsToRender = useMemo(() => {
    return skillsForReport.map(skillId => {
      const skill = (data || []).find(s => s.skillId === skillId);
      return (
        skill || {
          skillId: skillId,
          skillName: getSkillName(skillId).toString(),
          averageScore: 0,
          studentCount: 0,
        }
      );
    });
  }, [data]);

  return (
    <div className={styles.chart}>
      <div className={styles.yAxis}>
        {[100, 80, 60, 40, 20, 0].map(value => (
          <div key={value} className={styles.yAxisLabel}>
            {value}
          </div>
        ))}
      </div>
      <div className={styles.barsContainer}>
        <div className={styles.grid}>
          {[...Array(5)].map((_, rowIndex) =>
            [...Array(6)].map((_, colIndex) => (
              <div key={`${rowIndex}-${colIndex}`} className={styles.gridCell} />
            ))
          )}
        </div>
        <div className={styles.bars}>
          {skillsToRender.map(item => {
            const score = item.averageScore ? Math.min(Math.max(item.averageScore, 0), 100) : 0;

            return (
              <div key={item.skillName} className={styles.barWrapper}>
                <div className={styles.barContainer}>
                  <div
                    className={styles.barFill}
                    style={
                      {
                        height: '100%',
                        '--fill-height': `${score}%`,
                        '--fill-color': getScoreColor(score),
                        cursor: item.skillId ? 'pointer' : 'default',
                      } as React.CSSProperties & {
                        '--fill-height': string;
                        '--fill-color': string;
                      }
                    }
                    onClick={() => {
                      if (item.skillId) {
                        onSkillSelect?.(item.skillId);
                      }
                    }}
                  >
                    {score > 0 && <span className={styles.barValue}>{score}</span>}
                  </div>
                </div>
                <div className={styles.barLabel}>{getSkillLabel(item.skillId)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AvgScoresChar;
